/** Driver Support Chat — Firebase + demo flag (Vite .env and/or runtime Docker .env). */

declare global {
  interface Window {
    __VALT_CHAT_SUPPORT_ENV__?: Record<string, string>
  }
}

const CHAT_SUPPORT_KEYS = [
  'VITE_SUPPORT_DEMO',
  'VITE_CHAT_SUPPORT_API_KEY',
  'VITE_CHAT_SUPPORT_AUTH_DOMAIN',
  'VITE_CHAT_SUPPORT_PROJECT_ID',
  'VITE_CHAT_SUPPORT_STORAGE_BUCKET',
  'VITE_CHAT_SUPPORT_MESSAGING_SENDER_ID',
  'VITE_CHAT_SUPPORT_APP_ID',
] as const

const LEGACY_FIREBASE_MAP: Record<string, string> = {
  VITE_CHAT_SUPPORT_API_KEY: 'VITE_FIREBASE_API_KEY',
  VITE_CHAT_SUPPORT_AUTH_DOMAIN: 'VITE_FIREBASE_AUTH_DOMAIN',
  VITE_CHAT_SUPPORT_PROJECT_ID: 'VITE_FIREBASE_PROJECT_ID',
  VITE_CHAT_SUPPORT_STORAGE_BUCKET: 'VITE_FIREBASE_STORAGE_BUCKET',
  VITE_CHAT_SUPPORT_MESSAGING_SENDER_ID: 'VITE_FIREBASE_MESSAGING_SENDER_ID',
  VITE_CHAT_SUPPORT_APP_ID: 'VITE_FIREBASE_APP_ID',
}

const FIREBASE_REQUIRED_KEYS = [
  'VITE_CHAT_SUPPORT_API_KEY',
  'VITE_CHAT_SUPPORT_AUTH_DOMAIN',
  'VITE_CHAT_SUPPORT_PROJECT_ID',
  'VITE_CHAT_SUPPORT_STORAGE_BUCKET',
  'VITE_CHAT_SUPPORT_MESSAGING_SENDER_ID',
  'VITE_CHAT_SUPPORT_APP_ID',
] as const

function viteEnv(name: string): string {
  const runtime = typeof window !== 'undefined' ? window.__VALT_CHAT_SUPPORT_ENV__?.[name] : undefined
  if (runtime != null && String(runtime).trim() !== '') return String(runtime).trim()
  const fromVite = import.meta.env[name as keyof ImportMetaEnv]
  if (fromVite != null && String(fromVite).trim() !== '') return String(fromVite).trim()
  const legacy = LEGACY_FIREBASE_MAP[name]
  if (legacy) {
    const leg = import.meta.env[legacy as keyof ImportMetaEnv]
    if (leg != null && String(leg).trim() !== '') return String(leg).trim()
  }
  return ''
}

export function isSupportDemoModeFromEnv(): boolean {
  const flag = viteEnv('VITE_SUPPORT_DEMO').toLowerCase()
  return flag === 'true' || flag === '1'
}

export function missingChatSupportFirebaseEnvKeys(): string[] {
  if (isSupportDemoModeFromEnv()) return []
  return FIREBASE_REQUIRED_KEYS.filter((k) => !viteEnv(k))
}

export function assertChatSupportFirebaseEnv(): void {
  const missing = missingChatSupportFirebaseEnvKeys()
  if (missing.length === 0) return
  throw new Error(`Missing chat support Firebase env: ${missing.join(', ')}. Set them in .env`)
}

export function getChatSupportFirebaseConfig() {
  assertChatSupportFirebaseEnv()
  return {
    apiKey: viteEnv('VITE_CHAT_SUPPORT_API_KEY'),
    authDomain: viteEnv('VITE_CHAT_SUPPORT_AUTH_DOMAIN'),
    projectId: viteEnv('VITE_CHAT_SUPPORT_PROJECT_ID'),
    storageBucket: viteEnv('VITE_CHAT_SUPPORT_STORAGE_BUCKET'),
    messagingSenderId: viteEnv('VITE_CHAT_SUPPORT_MESSAGING_SENDER_ID'),
    appId: viteEnv('VITE_CHAT_SUPPORT_APP_ID'),
  }
}

/** Keys written by the nginx entrypoint from container environment / compose .env */
export const CHAT_SUPPORT_ENV_KEY_NAMES = CHAT_SUPPORT_KEYS
